import { Action, GameActionEnum, gameIsToday } from '@sportlogiq/shared';
import { isArray as _isArray } from 'lodash';
import { Team } from '../team/team.models';
import { VideoFile } from '@sportlogiq/main/shared/sl-types';

/**
 * @deprecated
 */
export class Video {
    id: string;
    frameRate: number;
    width: number;
    height: number;
    fileName: string;
    rootUrl: string;
    period: number;
    periodType: string;
}

/**
 * Based on V2 endpoints
 * @deprecated
 */
export class Game {
    id: string;
    awayTeamId: string;
    homeTeamId: string;
    date: string;
    dateTimezoned: Date;
    dateFromBackend: string;
    score: { [id: number]: number; }[];
    inProgress: boolean;
    seasonStage: string;
    seasonId: string;
    isShiftsCompleted: boolean;
    isCompleted: boolean;
    isPostponed: boolean;
    isLocked: boolean;
    seasonName: string;
    teams: { [id: string]: Team; };
    referenceId: string;
    sequenceId: string;
    tokenId: string;
    periods: number;
    isUpcoming: boolean;
    showAsUpcoming: boolean;
    isJustFinished: boolean;
    isFirstDataEntryComplete: boolean;
    isPassed: boolean;
    isToday: boolean;
    homeScore: number;
    awayScore: number;
    highScore: number;
    lowScore: number;
    periodScore: { [id: number]: number[]; }[];
    shootoutScore: { [id: number]: number; }[];
    isHome: boolean;
    isPlayoffs: boolean;
    isPreSeason: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metrics: any;
    lastPeriod: number;
    isOvertime: boolean;
    hasShootout: boolean;
    videos: VideoFile[];
    metricsFullyProcessed: boolean;

    get homeTeam() {
        return this.teams[this.homeTeamId];
    }

    get awayTeam() {
        return this.teams[this.awayTeamId];
    }

    get lastPeriodInGame() {
        return this.lastPeriod;
    }

    // eslint-disable-next-line complexity
    constructor(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        game: any,
        teams: { [id: string]: Team; }
    ) {
        this.id = game?.id;
        this.referenceId = game?.referenceId;
        this.sequenceId = game?.sequenceId;
        this.isShiftsCompleted = game?.isShiftsCompleted;
        this.isCompleted = game?.isCompleted;
        this.isPostponed = game?.isPostponed;
        this.isLocked = game?.isLocked;
        if (game?.tokenId) {
            this.tokenId = game.tokenId;
        }
        this.seasonStage = game?.season && game.season.stage;
        this.seasonId = game?.season && game.season.id;
        this.seasonName = game?.season && game.season.name;
        const date: string | Date = game?.scheduledTime ? game.scheduledTime : game?.date;
        /** @deprecated use dateFromBackend which will usually be UTC time */
        this.dateTimezoned = game && new Date(date);
        const offset = this.dateTimezoned.getTimezoneOffset();
        /** @deprecated use dateFromBackend which will usually be UTC time */
        this.date = new Date(this.dateTimezoned.getTime() - offset * 60 * 1000).toISOString().split('T')[0]; // Generating yyyy--mm-dd string format.
        this.dateFromBackend = game.date; // temporary workaround until we have proper UTC datetime support for all endpoints
        this.homeTeamId = game?.homeTeamId;
        this.awayTeamId = game?.awayTeamId;
        this.teams = teams;
        this.score = game?.score;
        this.periods = game && _isArray(game.periods) ? game.periods.length : game.periods;
        this.inProgress = game?.inProgress;
        this.homeScore = game?.score && game.score[this.homeTeamId];
        this.awayScore = game?.score && game.score[this.awayTeamId];
        this.highScore = Math.max(+this.homeScore, +this.awayScore);
        this.lowScore = Math.min(+this.homeScore, +this.awayScore);
        this.periodScore = game?.periodScore;
        this.shootoutScore = game?.shootoutScore;
        this.isHome = game?.isHomeGame;
        this.isFirstDataEntryComplete = game?.isFirstDataEntryComplete;
        this.lastPeriod = 3;
        this.hasShootout = game?.hasShootout;
        this.isOvertime = game?.periods > this.lastPeriod;
        this.metricsFullyProcessed = game.metricsFullyProcessed === '1';
        if (game.videos) {
            this.videos = game.videos;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        // It's necessary to check on the date being greater or equal to today because cancelled games.
        this.isUpcoming = game?.isUpcoming;
        this.showAsUpcoming = game?.showAsUpcoming;
        this.isPassed = game && !game.isUpcoming && !game.inProgress;
        this.isToday = game && gameIsToday(game.date);
        this.isJustFinished = this.isPassed && this.isToday;
        this.isPlayoffs = game?.season && game.season.stage === 'playoffs';
        this.isPreSeason = game?.season && game.season.stage === 'preseason';
        this.homeTeamId = game?.homeTeamId;
        this.awayTeamId = game?.awayTeamId;
        this.metrics = {
            opponentMetrics: [],
            teamMetrics: [],
        };
    }
}

/**
 * @deprecated
 */
export class GameAction extends Action<GameActionEnum, Game[]> {
    constructor(action: GameActionEnum, data: Game[] = []) {
        super(action, data);
    }
}
